import { useState } from 'react';
import { FormikProvider, getIn, useFormik } from 'formik';
import { FileTextOutlined, PlusOutlined } from '@ant-design/icons';
import { List, Space, Typography } from 'antd';
import { H3 } from 'components/new-ui/base/typography/styles';
import Button from 'components/new-ui/base/button/Button';
import CardWithIcon from 'components/new-ui/base/card/CardWithIcon';
import FormTextArea from 'components/new-ui/composed/formTextArea/FormTextArea';
import Notification from 'components/ui/base/notification/Notification';
import AddPaymentMethodModal from './AddPaymentMethodModal';
import useInvoicesConfig from 'hooks/useInvoicesConfig';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { useSyncDirty } from 'views/invoices/settings/tabs/GeneralSettingsDirtyContext';
import { getErrorMessage } from 'utils/getErrorMessage';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import { InvoiceTemplateFormFields, PaymentMethod, PaymentMethods } from './types';
import PaymentMethodItem from './PaymentMethodItem';
import { PaymentMethodDraft } from '../../validationSchemas';
import { invoiceTemplateValidationSchema } from './validationSchema';

function InvoiceTemplateSection() {
    const { config, updateInvoiceTemplate } = useInvoicesConfig();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();

    const [editingPaymentMethodIndex, setEditingPaymentMethodIndex] = useState<number | null>(null);
    const [editingPayentMethodItem, setEditingPayentMethodItem] = useState<PaymentMethodDraft | null>(null);
    const [showPaymentMethodsModal, setShowPaymentMethodsModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const formik = useFormik<InvoiceTemplateFormFields>({
        initialValues: {
            standardTerms: config?.standardTerms ?? null,
            paymentLink: config?.paymentLink ?? null,
            bankAccount: config?.bankAccount ?? [],
            paymentNotes: config?.paymentNotes ?? null,
        },
        validationSchema: invoiceTemplateValidationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                showGlobalOverlay({ type: 'LoadingOverlay' });
                setIsSaving(true);

                await updateInvoiceTemplate({
                    standardTerms: values?.standardTerms ?? null,
                    paymentLink: values?.paymentLink ?? null,
                    paymentNotes: values?.paymentNotes ?? null,
                    bankAccount: values.bankAccount ?? [],
                });

                setIsEditing(false);

                Notification({
                    type: 'success',
                    messageTitle: 'Update Complete',
                    description: 'Successfully updated configuration.',
                });
            } catch (error) {
                const errorMessage = getErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE;
                Notification({
                    type: 'error',
                    messageTitle: 'Update Failed',
                    description: errorMessage,
                });
            } finally {
                hideGlobalOverlay();
                setIsSaving(false);
            }
        },
    });

    const paymentTouched = getIn(formik.touched, 'paymentMethods');
    const paymentError = getIn(formik.errors, 'paymentMethods');
    const showPaymentError = !!paymentError && (!!paymentTouched || formik.submitCount > 0);

    const onAddPaymentMethod = () => {
        setEditingPaymentMethodIndex(null);
        setEditingPayentMethodItem(null);
        setShowPaymentMethodsModal(true);
    };

    const onEditPaymentMethod = (item: PaymentMethod) => {
        if (item.paymentMethod === PaymentMethods.PAY_PAL) {
            setEditingPaymentMethodIndex(null);
            setEditingPayentMethodItem({ paymentMethod: PaymentMethods.PAY_PAL, url: item.url });
        } else {
            const index = formik.values.bankAccount.indexOf(item);
            setEditingPaymentMethodIndex(index);
            setEditingPayentMethodItem({
                paymentMethod: PaymentMethods.BANK_TRANSFER,
                bankName: item.bankName,
                iban: item.iban,
                bic: item.bic,
            });
        }
        setShowPaymentMethodsModal(true);
    };

    const removePaymentMethod = (item: PaymentMethod) => {
        if (item.paymentMethod === PaymentMethods.PAY_PAL) {
            formik.setFieldValue('paymentLink', null);
        } else {
            formik.setFieldValue(
                'bankAccount',
                formik.values.bankAccount.filter((ba) => ba !== item)
            );
        }
        Notification({
            type: 'success',
            placement: 'top',
            description: 'Payment method removed successfully',
            messageTitle: 'Success',
        });
    };

    // Note: limited to one PayPal payment method; many bank transfers allowed
    const allPaymentMethods: PaymentMethod[] = [
        ...formik.values.bankAccount,
        ...(formik.values.paymentLink
            ? [{ paymentMethod: PaymentMethods.PAY_PAL as const, url: formik.values.paymentLink }]
            : []),
    ];
    const hasPaymentMethod = allPaymentMethods.length > 0;

    useSyncDirty('invoiceTemplate', formik.dirty);

    return (
        <FormikProvider value={formik}>
            <CardWithIcon
                title="Invoice Template"
                gap="large"
                iconProps={{
                    icon: <FileTextOutlined />,
                    status: 'success',
                }}
                headerActions={
                    <Space size={8}>
                        {isEditing && (
                            <Button
                                type="outlined"
                                disabled={isSaving}
                                onClick={() => {
                                    formik.resetForm();
                                    setIsEditing(false);
                                }}
                            >
                                Cancel
                            </Button>
                        )}
                        <Button
                            loading={isSaving}
                            style={{
                                color: 'var(--brand-6)',
                                borderColor: 'inherit',
                            }}
                            onClick={isEditing ? formik.submitForm : () => setIsEditing(true)}
                        >
                            {isSaving ? 'Saving' : isEditing ? 'Save' : 'Edit'}
                        </Button>
                    </Space>
                }
                children={
                    <div style={{ width: '100%', marginTop: 16 }}>
                        <div>
                            <H3>Payment Methods</H3>
                            <Typography.Text>
                                Add all valid payment methods and fill in all relevant details.
                            </Typography.Text>
                        </div>

                        {hasPaymentMethod && (
                            <List
                                style={{ marginTop: 16, paddingInline: 8 }}
                                dataSource={allPaymentMethods}
                                renderItem={(item: PaymentMethod) => (
                                    <PaymentMethodItem
                                        item={item}
                                        onEdit={() => onEditPaymentMethod(item)}
                                        onDelete={() => removePaymentMethod(item)}
                                        disabled={!isEditing}
                                    />
                                )}
                            />
                        )}

                        <Button
                            style={{ color: 'var(--brand-6)', marginTop: 12 }}
                            icon={<PlusOutlined />}
                            type="link"
                            disabled={!isEditing}
                            onClick={onAddPaymentMethod}
                        >
                            Add Payment Method
                        </Button>

                        {showPaymentError && <div style={{ color: 'var(--dust-red-6)' }}>{String(paymentError)}</div>}

                        <div style={{ marginTop: 24 }}>
                            <H3>Payment Notes and Additional Instructions</H3>
                            <Typography.Text>
                                Include any extra information about payment terms, conditions or special instructions
                                here
                            </Typography.Text>

                            <div>
                                <FormTextArea
                                    placeholder="e.g. To pay via PayPal or Card, click the 'Pay Now' Button + 3.5% Additional Charge Req"
                                    fieldProps={formik.getFieldProps('paymentNotes')}
                                    fieldMeta={formik.getFieldMeta('paymentNotes')}
                                    fieldHelper={formik.getFieldHelpers('paymentNotes')}
                                    style={{ marginTop: 8 }}
                                    autoSize={{ minRows: 4, maxRows: 16 }}
                                    disabled={!isEditing}
                                />
                            </div>
                        </div>

                        <div style={{ marginTop: 24 }}>
                            <H3>Standard Terms (Footer)</H3>
                            <Typography.Text>
                                Describe all standard terms that will be displayed on each invoice.
                            </Typography.Text>

                            <div>
                                <FormTextArea
                                    placeholder="Enter terms here"
                                    fieldProps={formik.getFieldProps('standardTerms')}
                                    fieldMeta={formik.getFieldMeta('standardTerms')}
                                    fieldHelper={formik.getFieldHelpers('standardTerms')}
                                    style={{ marginTop: 8 }}
                                    autoSize={{ minRows: 4, maxRows: 16 }}
                                    disabled={!isEditing}
                                />
                            </div>
                        </div>
                    </div>
                }
            />

            <AddPaymentMethodModal
                open={showPaymentMethodsModal}
                editingIndex={editingPaymentMethodIndex}
                initialItem={editingPayentMethodItem ?? null}
                onClose={() => {
                    setShowPaymentMethodsModal(false);
                    setEditingPaymentMethodIndex(null);
                }}
            />
        </FormikProvider>
    );
}

export default InvoiceTemplateSection;
